import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { forwardRef, memo } from "react";
import type { ComponentPropsWithoutRef } from "react";

import { ScrollButton } from "@/components/Button/ScrollButton";
import { TabsList } from "@/components/ui/tabs";
import { useScrollableNavigation } from "@/hooks/useScrollableNavigation";
import { cn } from "@/lib/cn";

type Props = ComponentPropsWithoutRef<typeof TabsList> & {
  /** ルート要素（relative）へ付与するクラス */
  rootClassName?: string;
  /** スクロールviewportへ付与するクラス */
  viewportClassName?: string;
};

/**
 * ScrollableTabsList
 *
 * `components/ui/tabs` の `TabsList` をラップし、
 * 幅不足時に段落ちせず 1 行のままスクロールできる TabsList を提供します。
 *
 * - オーバーフロー時のみ左右シェブロンを表示
 * - クリックで表示領域幅ぶんスクロール
 * - disabled 時はシェブロンを非表示（ScrollButtonの仕様）
 */
export const ScrollableTabsList = memo(
  forwardRef<HTMLDivElement, Props>(function ScrollableTabsList(
    { className, rootClassName, viewportClassName, ...props },
    ref,
  ) {
    const {
      containerRef,
      contentRef,
      handleScroll,
      scrollToPrevious,
      scrollToNext,
      isAtStart,
      isAtEnd,
    } = useScrollableNavigation();

    const composedRef = useComposedRefs(contentRef, ref);

    return (
      <div className={cn("flex relative min-w-0", rootClassName)}>
        <div
          ref={containerRef}
          className={cn("flex overflow-hidden w-full", viewportClassName)}
          onScroll={handleScroll}
        >
          <TabsList
            ref={composedRef}
            className={cn("flex-nowrap min-w-max", className)}
            {...props}
          />
        </div>

        <ScrollButton
          direction="left"
          onClick={scrollToPrevious}
          disabled={isAtStart}
          variant="bottomFill"
        />
        <ScrollButton
          direction="right"
          onClick={scrollToNext}
          disabled={isAtEnd}
          variant="bottomFill"
        />
      </div>
    );
  }),
);
